import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, ApiError } from '@/lib/api';

export type ResponseStyle = 'PLAIN_ENGLISH' | 'PRO';
export type Role = 'ADMIN' | 'USER';

export interface AuthUser {
  id: string;
  email: string;
  name?: string | null;
  role: Role;
  responseStyle: ResponseStyle;
}

export type AuthStatus = 'loading' | 'authed' | 'unauthed';

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /** Merge a partial update into the cached user (e.g. after changing the
   *  response style in Settings) without a full /me round trip. */
  updateUser: (patch: Partial<AuthUser>) => void;
}

const AuthContext = createContext<AuthState | null>(null);

interface MeResponse {
  user: AuthUser;
}

interface LoginResponse {
  user: AuthUser;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);

  const refresh = useCallback(async () => {
    try {
      const { user: me } = await api<MeResponse>('/auth/me');
      setUser(me);
      setStatus('authed');
    } catch (err) {
      // 401 is the expected "not logged in yet" path; anything else is also
      // treated as unauthed for safety. The login page surfaces real errors.
      if (!(err instanceof ApiError) || err.status !== 401) {
        console.warn('auth refresh failed', err);
      }
      setUser(null);
      setStatus('unauthed');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const { user: signedIn } = await api<LoginResponse>('/auth/login', {
      method: 'POST',
      body: { email, password },
    });
    setUser(signedIn);
    setStatus('authed');
  }, []);

  const logout = useCallback(async () => {
    try {
      await api<{ ok: true }>('/auth/logout', { method: 'POST' });
    } finally {
      setUser(null);
      setStatus('unauthed');
    }
  }, []);

  const updateUser = useCallback((patch: Partial<AuthUser>) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const value = useMemo<AuthState>(
    () => ({ status, user, login, logout, refresh, updateUser }),
    [status, user, login, logout, refresh, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return ctx;
}
